import { BackButton, Button, ClipSpinner } from '~/components/ui';
import { useTranslation } from 'react-i18next';
import { useGetOrderById } from '~/api';
import { OrderStatusBadge } from '~/components/ui/OrderStatusBadge';
import { assertDefined, calculateOrderTotal, formatDateTime } from '~/utils';
import { OrderProgressTrackerCard } from '~/components/cards/OrderProgessTrackerCard.tsx';
import { OrderSummaryCard } from '~/components/cards/OrderSummaryCard.tsx';
import { SellingInformationCard } from '~/components/cards/SellingInformationCard.tsx';
import { ShippingCard } from '~/components/cards/ShippingCard.tsx';
import { ItemCard } from '~/components/cards/ItemCard.tsx';
import { CheckCircle } from 'lucide-react';
import { Modal } from '~/components/overlays';
import { useState } from 'react';
import { useReceiveOrder } from '~/api';
import toast from 'react-hot-toast';
import { getGradeInformation } from '~/utils/order';

export function BuyerOrderView({ orderId }: { orderId: string }) {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const { mutate: receiveOrder, isPending: isReceivingOrder } =
    useReceiveOrder();

  const { t } = useTranslation('order');

  const { data, isLoading, isError, error } = useGetOrderById(orderId!);

  if (isLoading) return <ClipSpinner />;
  if (isError)
    return <div>{t('order.loading_error', { message: error.message })}</div>;

  assertDefined(data?.order, 'Order data should be defined');
  const order = data.order;

  const handleReceiveOrder = () => {
    receiveOrder(
      { orderId: order.id },
      {
        onSuccess: () => {
          setIsConfirmModalOpen(false);
          toast.success('Order delivery confirmed successfully');
        },
        onError: (error) => {
          toast.error(`Failed to confirm delivery: ${error.message}`);
        },
      }
    );
  };

  return (
    <div className="space-y-6">
      <Modal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
      >
        <DeliveryConfirmationDialog
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleReceiveOrder}
          isLoading={isReceivingOrder}
        />
      </Modal>
      <div>
        <BackButton link="/orders" label={t('order.back_button_label')} />
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl text-neutral-900 mb-2">
              {order.id}
            </h1>
            <p className="text-sm text-gray-600">
              {formatDateTime(order.createdAt)}
            </p>
          </div>
          <OrderStatusBadge status={order.status} size="small" />
        </div>
      </div>
      <div className="bg-neut-bg-alt p-6 rounded-lg flex flex-col gap-4 border border-neut-200 shadow-sm">
        <OrderProgressTrackerCard order={order} />
        {order.status?.toLowerCase() === 'shipped' ? (
          <Button onClick={() => setIsConfirmModalOpen(true)}>
            <CheckCircle size={16} /> Confirm Delivery
          </Button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-neutral-900 font-semibold">
                {t('order.order_items')}
              </h2>
            </div>
            {order.items.map((item) => (
              <ItemCard
                key={item.platformProductsLengths?.id}
                productName={
                  item.platformProductsLengths?.platformProduct.name ||
                  'not found'
                }
                itemQuantity={item.quantity.value}
                unit={item.quantity.unit}
                pricePerUnit={item.price}
                length={item.platformProductsLengths.length.displayValue}
                gradeInformation={getGradeInformation(
                  item.platformProductsLengths
                )}
              />
            ))}
          </div>
          <ShippingCard
            shippingLocationName={order.shippingLocation.name}
            addressLine1={order.shippingLocation.addressLine1}
            addressLine2={order.shippingLocation.addressLine2}
            region={order.shippingLocation.region}
            city={order.shippingLocation.city}
            zipCode={order.shippingLocation.zipCode}
          />
        </div>

        <div className="space-y-6">
          <SellingInformationCard
            sellingOrganizationName={order.sellingOrganization.name}
          />
          <OrderSummaryCard totalAmount={calculateOrderTotal(order)} />
        </div>
      </div>
    </div>
  );
}

// Delivery Confirmation Dialog Component
interface DeliveryConfirmationDialogProps {
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

export function DeliveryConfirmationDialog({
  isLoading = false,
  onConfirm,
  onClose,
}: Readonly<DeliveryConfirmationDialogProps>) {
  return (
    <div className="relative bg-neut-bg-alt rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
      {/* Success Icon */}
      <div className="w-12 h-12 rounded-full bg-success-200 flex items-center justify-center mb-4">
        <CheckCircle
          className="text-success-600"
          size={24}
          aria-hidden="true"
        />
      </div>

      {/* Dialog Content */}
      <h3 className="text-xl text-neut-prim font-semibold mb-2">
        Confirm Delivery
      </h3>

      <p className="text-sm text-neut-sec mb-6">
        Are you sure you want to confirm the delivery of this order? This action
        cannot be undone.
      </p>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button onClick={onClose} disabled={isLoading} variant="secondary">
          Keep Order
        </Button>

        <Button onClick={onConfirm} disabled={isLoading}>
          {isLoading ? 'Confirming...' : 'Yes, Confirm Delivery'}
        </Button>
      </div>
    </div>
  );
}
